import React, { useState, useRef, useEffect } from 'react';
import { formatFileSize, downloadFile, formatDuration } from '../../utils/mediaHelpers';
import { useBinaryMedia } from '../../utils/binaryMediaFetcher';

const VideoMessage = ({ media, caption, isSender }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('landscape');

  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Use binary media hook for proper blob fetching
  const { url: videoUrl, blobSize, isLoading: fetchLoading, error: fetchError, errorDetails } = useBinaryMedia(
    media?.url,
    'video/mp4,video/quicktime,video/webm,video/ogg,*/*'
  );

  // Video event handlers - only attach after binary blob is loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => {
      if (video.duration && video.duration !== Infinity) {
        setDuration(video.duration);
      }
    };

    const handleEnded = () => setIsPlaying(false);

    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded from binary blob:', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight
      });
      updateDuration();

      // Detect aspect ratio
      if (video.videoWidth && video.videoHeight) {
        const ratio = video.videoWidth / video.videoHeight;
        console.log('Video aspect ratio from binary blob:', ratio);

        if (ratio > 1.2) {
          setAspectRatio('landscape');
        } else if (ratio < 0.8) {
          setAspectRatio('portrait');
        } else {
          setAspectRatio('square');
        }
      }
    };

    const handleCanPlay = () => {
      console.log('Video can play - binary blob loaded successfully');
      setVideoError(false);
    };

    const handleError = (error) => {
      console.error('Video loading error with binary blob:', error, video.error);
      setVideoError(true);
    };

    // Add event listeners
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Fullscreen change listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [videoUrl]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(error => {
        console.error('Play failed:', error);
        setVideoError(true);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const seekTime = (e.nativeEvent.offsetX / e.target.offsetWidth) * duration;
    video.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    const video = videoRef.current;
    if (video) {
      video.volume = newVolume;
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleDownload = () => {
    downloadFile(media.url, media.originalName);
  };

  const handleMouseMove = () => {
    setShowControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const formatTime = (time) => {
    return formatDuration(time);
  };

  const calculateProgress = () => {
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  };

  // Try to reload video on error
  const handleRetry = () => {
    const video = videoRef.current;
    if (video) {
      setVideoError(false);
      video.load();
    }
  };

  const isLoading = fetchLoading || (videoUrl && !duration);

  return (
    <div
      className={`media-message video-message ${isSender ? 'sender' : 'receiver'} ${isFullscreen ? 'fullscreen' : ''} ${aspectRatio}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
      style={{ width: '100%', maxWidth: '300px' }}
    >
      <div className="video-container">
        {/* Video element with binary blob URL */}
        {videoUrl && (
          <video
            ref={videoRef}
            className="video-player"
            src={videoUrl}
            controls={false} // We use custom controls
            playsInline
            preload="metadata"
            onClick={togglePlay}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: isLoading || videoError ? 'none' : 'block'
            }}
          >
            Your browser does not support the video tag.
          </video>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="video-loading">
            <div className="loading-spinner"></div>
            <span>Loading video...</span>
            {blobSize > 0 && (
              <div className="video-info">
                <small>Size: {formatFileSize(blobSize)}</small>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Error state */}
        {(videoError || fetchError) && (
          <div className="video-error">
            <div className="error-icon">❌</div>
            <span>Failed to load video</span>
            {errorDetails && (
              <div className="error-details">
                <small>{errorDetails}</small>
              </div>
            )}
            <div className="error-actions">
              <button
                className="download-fallback-btn"
                onClick={handleDownload}
                title="Download video file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                Download Video
              </button>
              <button
                className="retry-btn"
                onClick={handleRetry}
                title="Try to load video again"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                </svg>
                Retry
              </button>
            </div>
            <div className="error-tips">
              <small>Tip: The video file is still available for download</small>
            </div>
          </div>
        )}

        {/* Custom video controls */}
        {(showControls || !isPlaying) && !isLoading && !videoError && videoUrl && (
          <div className="video-controls">
            {/* Play/Pause overlay */}
            {!isPlaying && (
              <button
                className="video-play-overlay"
                onClick={togglePlay}
              >
                <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}

            {/* Bottom controls bar */}
            <div className="video-controls-bar">
              {/* Play/Pause button */}
              <button
                className="video-control-btn"
                onClick={togglePlay}
              >
                {isPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Progress bar */}
              <div className="video-progress-container" onClick={handleSeek}>
                <div
                  className="video-progress-bar"
                  style={{ width: `${calculateProgress()}%` }}
                />
                <div className="video-progress-background" />
              </div>

              {/* Time display */}
              <div className="video-time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>

              {/* Volume control */}
              <div className="video-volume-container">
                <button
                  className="video-control-btn"
                  title="Volume"
                >
                  {volume === 0 ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 9H6c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h3.29l4.71 4.71c.62.62 1.7.18 1.7-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91v2.06c1.3-.35 2.48-.97 3.47-1.81l1.29 1.29c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L5.05 3.63a.996.996 0 00-1.41 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v2.06c2.89.86 5 3.54 5 6.58z" />
                    </svg>
                  ) : volume < 0.5 ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="volume-slider"
                />
              </div>

              {/* Fullscreen button */}
              <button
                className="video-control-btn"
                onClick={toggleFullscreen}
                title="Fullscreen"
              >
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Download button */}
        <button
          className="media-download-btn"
          onClick={handleDownload}
          title="Download video"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
          </svg>
        </button>
      </div>

      {/* Metadata - timestamp only */}
      <div className="media-info">
        <div className="media-metadata">
          {blobSize > 0 && (
            <div className="file-size">{formatFileSize(blobSize)}</div>
          )}
        </div>
        <div className="message-time">
          {new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
};

export default VideoMessage;
